// The 5 report tables. Column keys match the API's view columns (labels.ru on the backend gives
// the same RU headers). `type` drives cell formatting + whether a column is summed when grouped.

export type ColType = "text" | "name" | "num" | "money" | "date" | "badge";

export interface ColSpec {
  key: string;
  label: string;
  type: ColType;
  w: number; // default width px
}

export interface ColGroup {
  label: string;
  keys: string[];
}

export interface ReportSpec {
  key: ReportKey;
  title: string;
  view: string;
  time: boolean; // has a date grain (date range presets apply)
  apiPath: string; // /api/v1 list path
  columns: ColSpec[];
  groups?: ColGroup[]; // optional super-headers (shown when members are visible & contiguous)
  defaultVisible: string[];
  defaultPinned: string[];
  defaultGroupBy?: string[]; // opens grouped (collapsed) by these columns
  defaultSort?: Array<{ key: string; dir: "asc" | "desc" }>; // also orders the groups
  rowKey: (row: Row) => string;
}

export type ReportKey = "current" | "daily" | "weekly" | "monthly" | "suppliers" | "brands";
export type Row = Record<string, string | number | null>;

const W = { text: 156, name: 250, num: 126, money: 144, date: 116, badge: 138 };
// Header width must fit its OWN label (+ the sort arrow / filter icon it carries) — a flat W[type]
// truncated long headers like "Остаток на начало" or "Себестоимость (средневзв.)" to "Остат…" even
// with room to spare on screen (2026-09-11 UI review: "jadvallarni ko'rishda muammo ... yaxshi
// ko'rinmayapti"). Widens only labels that need it; short ones ("Брак") still get the compact W[type].
// The +88 "chrome" constant is measured, not guessed: every header carries ~83px of non-text
// content (left padding + the filter-button slot + the resize handle) regardless of column type —
// confirmed via getBoundingClientRect() on a live table (all 7 default "current" columns landed
// within 1px of exactly 83px of header width the label text itself didn't use).
const c = (key: string, label: string, type: ColType): ColSpec => ({
  key,
  label,
  type,
  w: Math.min(340, Math.max(W[type], Math.ceil(label.length * 6.3) + 88)),
});

// columns that must never be summed in a group aggregate (rates / period labels)
export const NO_AGGREGATE = new Set(["last_unit_cost", "unit_cost", "year", "month"]);

// Every resizable column (flat table, pivot table) can be dragged down to this floor and no
// smaller — 2cm @ 96dpi (the CSS reference px), per explicit user request ("har qanday jadvalni
// kichraytirib bo'lsin 2 sm gacha", 2026-09-11).
export const MIN_COL_W = 76;

// "Текущий остаток" (current) only: negative values are shown as 0 for readability (the sheet
// keeps a handful of products with a negative running balance) — DISPLAY ONLY. The API returns
// the true value so sums/filters/sort/calc columns/CSV export stay correct; only fmt() in
// ReportTable floors it for the cell text. 2026-09-11: moved here from the API, because floored
// row data made the table's own "Итого" (sum of already-0'd cells) disagree with the Обзор KPI
// cards, which read the true SUM straight from SQL.
export const DISPLAY_FLOOR_CURRENT = new Set([
  "closing_stock", "available_to_ship", "stock_value", "available_value",
]);

export const GROUPINGS: Array<{ by: string[]; label: string }> = [
  { by: [], label: "Без группировки" },
  { by: ["category"], label: "По категории" },
  { by: ["category", "brand"], label: "Категория → бренд" },
  { by: ["brand"], label: "По бренду" },
  { by: ["product_name"], label: "По товару" },
  // only offered where the report has a `supplier` column (GroupMenu filters by known columns)
  { by: ["supplier"], label: "По поставщику" },
  { by: ["supplier", "category"], label: "Поставщик → категория" },
];

export const REPORTS: Record<ReportKey, ReportSpec> = {
  current: {
    key: "current",
    title: "Текущий остаток",
    // display label only (shown on "Все отчёты"); the API actually reads vw_stock_current_web —
    // vw_stock_current is Power BI's own import and isn't what this site's numbers come from
    // (2026-09-11 split). Corrected 2026-09-12 UI review — was showing the wrong view name.
    view: "vw_stock_current_web",
    time: false,
    apiPath: "/products",
    columns: [
      c("product_name", "Наименование товара", "name"),
      c("product_id", "Товар_ID", "text"),
      c("category", "Категория", "text"),
      c("brand", "Бренд", "text"),
      c("model", "Модель", "text"),
      c("storage", "Память", "text"),
      c("color", "Цвет", "text"),
      c("closing_stock", "Остаток на конец", "num"),
      c("available_to_ship", "Доступно к отправке", "num"),
      c("ready_to_ship", "Готово к отправке", "num"),
      c("last_unit_cost", "Себестоимость (средневзв.)", "money"),
      c("stock_value", "Сумма остатка", "money"),
    ],
    defaultVisible: [
      "product_name", "category", "brand", "closing_stock", "available_to_ship", "last_unit_cost", "stock_value",
    ],
    defaultPinned: ["product_name"],
    rowKey: (r) => String(r.product_id),
  },
  daily: {
    key: "daily",
    title: "По дням",
    view: "vw_daily_stock",
    time: true,
    apiPath: "/analytics/daily",
    columns: [
      c("date", "Дата", "date"),
      c("product_name", "Наименование товара", "name"),
      c("category", "Категория", "text"),
      c("brand", "Бренд", "text"),
      c("opening_stock", "Остаток на начало", "num"),
      c("supply", "Поставка (кол-во)", "num"),
      c("shipment", "Отгрузка (кол-во)", "num"),
      c("defect", "Брак", "num"),
      c("return_qty", "Возврат кол-во", "num"),
      c("closing_stock", "Остаток на конец", "num"),
      c("ready_to_ship", "Готово к отправке", "num"),
      c("available_to_ship", "Доступно к отправке", "num"),
      c("supply_value", "Сумма поставки", "money"),
      c("shipment_value", "Сумма отгрузки", "money"),
      c("stock_value", "Сумма остатка", "money"),
      c("unit_cost", "Себестоимость", "money"),
      c("supply_order_ids", "Заявки (поставка)", "text"),
      c("shipment_order_ids", "Заявки (отгрузка)", "text"),
    ],
    groups: [
      { label: "Движение (кол-во)", keys: ["supply", "shipment", "defect", "return_qty"] },
      { label: "Остаток", keys: ["opening_stock", "closing_stock", "ready_to_ship", "available_to_ship"] },
      { label: "Суммы", keys: ["supply_value", "shipment_value", "stock_value", "unit_cost"] },
      { label: "Заявки", keys: ["supply_order_ids", "shipment_order_ids"] },
    ],
    defaultVisible: [
      "date", "product_name", "opening_stock", "supply", "shipment", "defect", "return_qty", "closing_stock", "stock_value",
    ],
    defaultPinned: ["date"],
    rowKey: (r) => `${r.date}·${r.product_id}`,
  },
  weekly: {
    key: "weekly",
    title: "По неделям",
    view: "vw_weekly_stock",
    time: true,
    apiPath: "/analytics/weekly",
    columns: [
      c("week_label", "Неделя", "text"),
      c("week_start", "Начало недели", "date"),
      c("week_end", "Конец недели", "date"),
      c("product_name", "Наименование товара", "name"),
      c("category", "Категория", "text"),
      c("opening_stock", "Остаток на начало", "num"),
      c("supply", "Поставка", "num"),
      c("shipment", "Отгрузка", "num"),
      c("return_qty", "Возврат", "num"),
      c("defect", "Брак", "num"),
      c("closing_stock", "Остаток на конец", "num"),
      c("stock_value", "Сумма остатка", "money"),
      c("supply_order_ids", "Заявки (поставка)", "text"),
      c("shipment_order_ids", "Заявки (отгрузка)", "text"),
    ],
    groups: [
      { label: "Движение", keys: ["supply", "shipment", "return_qty", "defect"] },
      { label: "Заявки", keys: ["supply_order_ids", "shipment_order_ids"] },
    ],
    defaultVisible: [
      "week_start", "week_end", "product_name", "opening_stock", "supply", "shipment", "return_qty", "defect", "closing_stock", "stock_value",
    ],
    defaultPinned: ["week_start"],
    rowKey: (r) => `${r.week_start}·${r.product_id}`,
  },
  monthly: {
    key: "monthly",
    title: "По месяцам",
    view: "vw_monthly_stock",
    time: true,
    apiPath: "/analytics/monthly",
    columns: [
      c("month_name", "Месяц", "text"),
      c("year", "Год", "num"),
      c("product_name", "Наименование товара", "name"),
      c("category", "Категория", "text"),
      c("opening_stock", "Остаток на начало", "num"),
      c("supply", "Поставка", "num"),
      c("shipment", "Отгрузка", "num"),
      c("return_qty", "Возврат", "num"),
      c("defect", "Брак", "num"),
      c("closing_stock", "Остаток на конец", "num"),
      c("stock_value", "Сумма остатка", "money"),
      c("supply_order_ids", "Заявки (поставка)", "text"),
      c("shipment_order_ids", "Заявки (отгрузка)", "text"),
    ],
    groups: [
      { label: "Движение", keys: ["supply", "shipment", "return_qty", "defect"] },
      { label: "Заявки", keys: ["supply_order_ids", "shipment_order_ids"] },
    ],
    defaultVisible: [
      "month_name", "year", "product_name", "opening_stock", "supply", "shipment", "return_qty", "defect", "closing_stock", "stock_value",
    ],
    defaultPinned: ["month_name"],
    rowKey: (r) => `${r.year}-${r.month}·${r.product_id}`,
  },
  // vw_stock_by_supplier: «Текущий остаток» split by the sheet's «Поставщик» (exact — the sheet
  // keeps остаток per supply row). One row per supplier × product; opens grouped by supplier so
  // the first screen is the per-supplier totals (soni / summasi).
  suppliers: {
    key: "suppliers",
    title: "Остаток по поставщикам",
    view: "vw_stock_by_supplier",
    time: false,
    apiPath: "/suppliers",
    columns: [
      { ...c("supplier", "Поставщик", "text"), w: 260 }, // names like «Samsung Elektronics (Sardor)»
      c("product_name", "Наименование товара", "name"),
      c("product_id", "Товар_ID", "text"),
      c("category", "Категория", "text"),
      c("brand", "Бренд", "text"),
      c("model", "Модель", "text"),
      c("storage", "Память", "text"),
      c("color", "Цвет", "text"),
      c("closing_stock", "Остаток", "num"),
      c("available_to_ship", "Доступно к отправке", "num"),
      c("last_unit_cost", "Себестоимость (средневзв.)", "money"),
      c("stock_value", "Сумма остатка", "money"),
    ],
    defaultVisible: ["supplier", "product_name", "category", "brand", "closing_stock", "last_unit_cost", "stock_value"],
    defaultPinned: ["supplier"],
    defaultGroupBy: ["supplier"],
    defaultSort: [{ key: "stock_value", dir: "desc" }],
    rowKey: (r) => `${r.supplier}·${r.product_id}`,
  },
  // vw_stock_current_web (same numbers as «Текущий остаток») opened grouped by brand — the first
  // screen is the per-brand totals; expand a brand to see its products.
  brands: {
    key: "brands",
    title: "Остаток по брендам",
    view: "vw_stock_current_web",
    time: false,
    apiPath: "/brands",
    columns: [
      c("brand", "Бренд", "text"),
      c("product_name", "Наименование товара", "name"),
      c("product_id", "Товар_ID", "text"),
      c("category", "Категория", "text"),
      c("model", "Модель", "text"),
      c("storage", "Память", "text"),
      c("color", "Цвет", "text"),
      c("closing_stock", "Остаток на конец", "num"),
      c("available_to_ship", "Доступно к отправке", "num"),
      c("last_unit_cost", "Себестоимость (средневзв.)", "money"),
      c("stock_value", "Сумма остатка", "money"),
    ],
    defaultVisible: ["brand", "product_name", "category", "closing_stock", "available_to_ship", "last_unit_cost", "stock_value"],
    defaultPinned: ["brand"],
    defaultGroupBy: ["brand"],
    defaultSort: [{ key: "stock_value", dir: "desc" }],
    rowKey: (r) => String(r.product_id),
  },
};

export const REPORT_ORDER: ReportKey[] = ["current", "daily", "weekly", "monthly"];
